import { useEffect, useState } from 'react';
import { createStandaloneToast } from '@chakra-ui/toast';

import type { MessagesType, MessageType } from '@izimi/modules';
import theme from '@izimi/theme';

import type { FeatherIconName } from '../icon/FeatherIcon';
import Message from './Message';

type TMessage = MessageType & { translateMessage?: boolean };

const { toast, ToastContainer } = createStandaloneToast({
  theme,
  defaultOptions: { duration: 3000, position: 'top' },
});

export { ToastContainer };

export const showSuccessMessage = (options: TMessage) =>
  toast({
    render: (props) => (
      <Message
        {...props}
        title={options.message}
        translateTitle={options.translateMessage}
        status='success'
        icon={(options.icon as FeatherIconName) ?? 'check-square'}
      />
    ),
  });

export const showErrorMessage = (options: TMessage) =>
  toast({
    render: (props) => (
      <Message
        {...props}
        title={options.message}
        translateTitle={options.translateMessage}
        status='error'
        icon={(options.icon as FeatherIconName) ?? 'alert-triangle'}
      />
    ),
  });

export const withMessages =
  <T extends Promise<Awaited<T>>>(fn: T) =>
  async ({ initialMessage, successMessage, errorMessage }: MessagesType = {}) => {
    try {
      if (initialMessage) {
        showSuccessMessage(initialMessage);
      }
      const res = await fn;
      if (successMessage) {
        showSuccessMessage(
          typeof successMessage === 'function' ? successMessage(res) : successMessage,
        );
      }
      return res;
    } catch (err) {
      if (errorMessage) {
        showErrorMessage(
          typeof errorMessage === 'function' ? await errorMessage(err) : errorMessage,
        );
      }
      throw err;
    }
  };

export const useWithMessages =
  <T extends { error: any; isSuccess: boolean; [key: string]: any }>(
    hook: T, // eslint-disable @typescript-eslint/no-explicit-any
  ) =>
  (messages: Parameters<ReturnType<typeof withMessages>>['0']) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [initialShown, setInitialShown] = useState(false);
    const { error, isSuccess, ...rest } = hook;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      (async function () {
        if (!initialShown && messages?.initialMessage) {
          showSuccessMessage(messages.initialMessage);
          setInitialShown(true);
        }
        if (isSuccess && messages?.successMessage) {
          showSuccessMessage(
            typeof messages.successMessage === 'function'
              ? messages.successMessage()
              : messages.successMessage,
          );
        }
        if (error && messages?.errorMessage) {
          showErrorMessage(
            typeof messages.errorMessage === 'function'
              ? await messages.errorMessage(error)
              : messages.errorMessage,
          );
        }
      })();
    }, [error, isSuccess, messages, initialShown]);

    return {
      error,
      isSuccess,
      ...rest,
    };
  };
